import { apiClient } from '../api-client';
import { TokenService } from '../auth/token.service';
import { apiConfig } from '../../config/app.config';

interface CachedPhoto {
  imageUrl?: string;
  hasProfilePicture: boolean;
  expiresAt: number; // Unix timestamp in ms — when the presigned URL expires
}

// Session-level cache: userId → CachedPhoto. Persists until page reload (logout).
const photoCache = new Map<string, CachedPhoto>();

// In-flight request cache: stores pending getProfileImage() promises by userId
// Prevents duplicate simultaneous API calls for the same user
const inFlightRequests = new Map<string, Promise<ProfileImageData>>();

const CACHE_BUFFER_MS = 5 * 60 * 1000; // expire 5 min early to avoid serving a just-expired URL

// PRD-01: same required REACT_APP_API_BASE_URL as apiClient (was REACT_APP_API_URL, unset everywhere, with a dev fallback)
const API_BASE_URL = apiConfig.baseUrl;

export interface ProfileImageUploadResponse {
  message: string;
  data: {
    success: boolean;
    userId: string;
    profilePictureUrl: string;
  };
}

export interface ProfileImageGetResponse {
  message: string;
  data: {
    success: boolean;
    userId: string;
    hasProfilePicture: boolean;
    profilePictureUrl?: string;
    expiresAt?: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface ProfileImageData {
  userId: string;
  imageUrl: string | null;
  hasProfilePicture: boolean;
  expiresAt?: string;
}

/**
 * Get the in-memory access token
 */
const getAuthToken = (): string => {
  const token = TokenService.getAccessToken();
  if (!token) {
    throw new Error('Authentication token not found');
  }
  return token;
};

/**
 * Convert a File or Blob to base64 string
 */
const fileToBase64 = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};

export class ProfileImageService {
  /**
   * Upload a profile image for a user
   * @param userId - The user's unique identifier
   * @param imageFile - The image file to upload (File or Blob)
   * @param contentType - The MIME type of the image (e.g., 'image/jpeg')
   * @returns Promise with the uploaded image URL
   */
  async uploadProfileImage(
    userId: string, 
    imageFile: File | Blob, 
    contentType: string = 'image/jpeg'
  ): Promise<ProfileImageUploadResponse> {
    try {
      console.log('uploadProfileImage called with userId:', userId);
      
      if (!userId) {
        throw new Error('User ID is required for profile image upload');
      }
      
      const token = getAuthToken();
      
      // Convert file to base64
      const base64Image = await fileToBase64(imageFile);
      
      const uploadUrl = `${API_BASE_URL}/users/${encodeURIComponent(userId)}/profile/uploadpicture`;
      console.log('Uploading to URL:', uploadUrl);

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: base64Image,
          contentType: contentType,
        }),
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      // Invalidate cache so next call fetches the fresh URL
      photoCache.delete(userId);
      return response.json();
    } catch (error) {
      console.error('Error uploading profile image:', error);
      throw new Error('Failed to upload profile image');
    }
  }

  /**
   * Get the profile image URL for the authenticated user
   * User is identified by the Bearer token, no userId parameter needed
   * Uses request deduplication to prevent simultaneous duplicate calls
   * @returns Promise with the user's profile image data
   */
  async getProfileImage(): Promise<ProfileImageData> {
    try {
      const token = getAuthToken();
      // Extract userId from token (JWT payload) for deduplication key
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid JWT token format');
      }
      const decoded = JSON.parse(atob(tokenParts[1]));
      const userId = decoded.sub || decoded['cognito:username'] || '';

      // Request deduplication: if a request for this user is already in-flight, return that promise
      if (inFlightRequests.has(userId)) {
        console.log(`📦 Reusing in-flight request for user ${userId}`);
        return inFlightRequests.get(userId)!;
      }

      // Create the actual API call promise
      const requestPromise = (async (): Promise<ProfileImageData> => {
        try {
          const response = await fetch(`${API_BASE_URL}/users/profile/picture`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error(`Fetch failed: ${response.statusText}`);
          }

          const result: ProfileImageGetResponse = await response.json();
          
          return {
            userId: result.data.userId,
            imageUrl: result.data.hasProfilePicture ? result.data.profilePictureUrl || null : null,
            hasProfilePicture: result.data.hasProfilePicture,
            expiresAt: result.data.expiresAt,
          };
        } finally {
          // Clean up in-flight cache after request completes (success or error)
          inFlightRequests.delete(userId);
        }
      })();

      // Store the in-flight promise
      inFlightRequests.set(userId, requestPromise);
      return requestPromise;
    } catch (error) {
      console.error('Error fetching profile image:', error);
      return {
        userId: '',
        imageUrl: null,
        hasProfilePicture: false,
      };
    }
  }

  /**
   * Delete the profile image for a user
   * @param userId - The user's unique identifier
   * @returns Promise with success status
   */
  async deleteProfileImage(userId: string): Promise<{ success: boolean }> {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/users/profile-image/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }

      // Invalidate cache so the deleted photo isn't served from cache
      photoCache.delete(userId);
      return response.json();
    } catch (error) {
      console.error('Error deleting profile image:', error);
      throw new Error('Failed to delete profile image');
    }
  }

  /**
   * Fetch a provider's profile photo URL by their user ID.
   * Uses apiClient (authenticated). Never throws — returns { hasProfilePicture: false } on any failure.
   */
  static async getProviderProfileImage(
    userId: string
  ): Promise<{ imageUrl?: string; hasProfilePicture: boolean }> {
    // Check cache first — return if entry exists and hasn't expired (with buffer)
    const cached = photoCache.get(userId);
    if (cached && cached.expiresAt - CACHE_BUFFER_MS > Date.now()) {
      return { imageUrl: cached.imageUrl, hasProfilePicture: cached.hasProfilePicture };
    }

    try {
      const response = await apiClient.get<{
        userId?: string;
        hasProfilePicture: boolean;
        profilePictureUrl?: string;
        expiresAt?: string;
      }>(`/users/${userId}/profile/picture`);

      if (response.success && response.data) {
        const { hasProfilePicture, profilePictureUrl, expiresAt } = response.data;

        if (hasProfilePicture) {
          const expiryMs = expiresAt
            ? new Date(expiresAt).getTime()
            : Date.now() + 55 * 60 * 1000;
          photoCache.set(userId, {
            imageUrl: profilePictureUrl,
            hasProfilePicture: true,
            expiresAt: expiryMs,
          });
          return { hasProfilePicture: true, imageUrl: profilePictureUrl };
        }

        // No photo — cache briefly to avoid hammering the API
        photoCache.set(userId, {
          hasProfilePicture: false,
          expiresAt: Date.now() + 10 * 60 * 1000,
        });
        return { hasProfilePicture: false };
      }
      return { hasProfilePicture: false };
    } catch {
      // Do not cache on error — allow retry on next mount
      return { hasProfilePicture: false };
    }
  }
}

export const profileImageService = new ProfileImageService();

export async function getProviderProfileImage(
  userId: string
): Promise<{ imageUrl?: string; hasProfilePicture: boolean }> {
  return ProfileImageService.getProviderProfileImage(userId);
}

/**
 * Clear the session-level photo cache. Call on logout to ensure stale URLs
 * are not served to the next user on a shared device.
 */
export function clearPhotoCache(): void {
  photoCache.clear();
}
